// ============================================================================
// SANDBOX TAB - Project sandbox environment management
// State-driven display: request → pending → provisioning → active → deployed
// ============================================================================

import React, { useState } from 'react';
import {
  Cloud,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  Copy,
  ExternalLink,
  AlertTriangle,
  Trash2,
  RefreshCw,
  Globe,
  Server,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSandboxStatus, useRequestSandbox, useDecommissionSandbox } from '../../hooks/useSandbox';


interface SandboxTabProps {
  projectId: string;
  token: string;
}

export const SandboxTab: React.FC<SandboxTabProps> = ({ projectId }) => {
  const { user } = useAuth();
  const { data, isLoading, error, refetch } = useSandboxStatus(projectId);
  const requestMutation = useRequestSandbox(projectId);
  const decommissionMutation = useDecommissionSandbox(projectId);

  const [budgetLimit, setBudgetLimit] = useState<number>(100);
  const [justification, setJustification] = useState('');
  const [copied, setCopied] = useState(false);

  const isAdmin = user?.role === 'system_admin';
  const env = data?.sandboxEnvironment;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRequest = async () => {
    try {
      await requestMutation.mutateAsync({
        provider: 'aws',
        budgetLimit,
        justification: justification || undefined,
        region: 'eu-west-1',
      });
    } catch (err: any) {
      // Error handled by mutation state
    }
  };

  const handleDecommission = async () => {
    if (!window.confirm('Are you sure you want to decommission this sandbox? This will destroy all resources in the child account.')) return;
    try {
      await decommissionMutation.mutateAsync();
    } catch (err: any) {
      // Error handled by mutation state
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 0' }}>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: 8, color: '#64748b' }}>Loading sandbox status...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>
        <AlertTriangle size={32} style={{ margin: '0 auto 12px' }} />
        <p>Failed to load sandbox status</p>
        <button onClick={() => refetch()} style={linkBtnStyle}>
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    );
  }

  // No environment or decommissioned — show request form
  if (!env || env.status === 'none' || env.status === 'decommissioned') {
    return (
      <div style={{ maxWidth: 600 }}>
        <SectionHeader icon={Cloud} title="Request Sandbox Environment" />
        <p style={{ color: '#64748b', fontSize: 14, marginBottom: 24 }}>
          A sandbox environment provides a dedicated AWS account for your project with isolated infrastructure, budget controls, and SSO access.
        </p>

        {env?.status === 'decommissioned' && (
          <div style={{ ...bannerStyle, background: '#f1f5f9', borderColor: '#cbd5e1', marginBottom: 20 }}>
            Previous environment was decommissioned on {env.decommissionedAt ? new Date(env.decommissionedAt).toLocaleDateString() : 'unknown date'}.
          </div>
        )}

        {env?.provisioningError && env.status === 'none' && (
          <div style={{ ...bannerStyle, background: '#fef2f2', borderColor: '#fecaca', marginBottom: 20, color: '#b91c1c' }}>
            Previous request was rejected: {env.provisioningError}
          </div>
        )}

        {/* Provider selector */}
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Cloud Provider</label>
          <div style={{ display: 'flex', gap: 12 }}>
            <ProviderCard name="AWS" selected disabled={false} />
            <ProviderCard name="Azure" selected={false} disabled />
            <ProviderCard name="GCP" selected={false} disabled />
          </div>
        </div>

        {/* Budget */}
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Monthly Budget Limit (USD)</label>
          <input
            type="number"
            value={budgetLimit}
            onChange={(e) => setBudgetLimit(Number(e.target.value))}
            min={10}
            max={10000}
            style={inputStyle}
          />
        </div>

        {/* Justification */}
        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>Justification (optional)</label>
          <textarea
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="Why does this project need a sandbox environment?"
            rows={3}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        <button
          onClick={handleRequest}
          disabled={requestMutation.isPending}
          style={primaryBtnStyle}
        >
          {requestMutation.isPending ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Requesting...</> : 'Request Sandbox Environment'}
        </button>

        {requestMutation.isError && (
          <p style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{requestMutation.error.message}</p>
        )}
      </div>
    );
  }

  // Requested — pending approval
  if (env.status === 'requested') {
    return (
      <div style={{ maxWidth: 600 }}>
        <SectionHeader icon={Clock} title="Pending Approval" />
        <div style={{ ...bannerStyle, background: '#fffbeb', borderColor: '#fde68a' }}>
          <Clock size={16} style={{ color: '#d97706', flexShrink: 0 }} />
          <div>
            <strong style={{ color: '#92400e' }}>Awaiting admin approval</strong>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#78716c' }}>
              Requested {env.requestedAt ? new Date(env.requestedAt).toLocaleString() : 'recently'} &middot; Budget: ${env.budgetLimit}/month &middot; Provider: {env.provider.toUpperCase()}
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#78716c' }}>
              Estimated provisioning time: ~5 minutes after approval
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Provisioning
  if (env.status === 'provisioning' || env.status === 'approved') {
    return (
      <div style={{ maxWidth: 600 }}>
        <SectionHeader icon={Loader2} title="Provisioning" spinning />
        <div style={{ ...bannerStyle, background: '#eff6ff', borderColor: '#bfdbfe' }}>
          <Loader2 size={16} style={{ color: '#2563eb', flexShrink: 0, animation: 'spin 1s linear infinite' }} />
          <div>
            <strong style={{ color: '#1e40af' }}>Setting up your sandbox environment...</strong>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
              This typically takes ~5 minutes. Creating AWS account, configuring VPC, CloudTrail, budgets, and SSO.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Failed
  if (env.status === 'failed') {
    return (
      <div style={{ maxWidth: 600 }}>
        <SectionHeader icon={XCircle} title="Provisioning Failed" />
        <div style={{ ...bannerStyle, background: '#fef2f2', borderColor: '#fecaca' }}>
          <AlertTriangle size={16} style={{ color: '#dc2626', flexShrink: 0 }} />
          <div>
            <strong style={{ color: '#991b1b' }}>Environment setup failed</strong>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#78716c' }}>
              {env.provisioningError || 'An unknown error occurred during provisioning.'}
            </p>
            {isAdmin && (
              <button onClick={handleRequest} style={{ ...linkBtnStyle, marginTop: 8 }}>
                <RefreshCw size={14} /> Retry
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Decommissioning
  if (env.status === 'decommissioning') {
    return (
      <div style={{ maxWidth: 600 }}>
        <SectionHeader icon={Loader2} title="Decommissioning" spinning />
        <div style={{ ...bannerStyle, background: '#fef3c7', borderColor: '#fde68a' }}>
          <Loader2 size={16} style={{ color: '#d97706', flexShrink: 0, animation: 'spin 1s linear infinite' }} />
          <div>
            <strong style={{ color: '#92400e' }}>Decommissioning sandbox...</strong>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
              Removing SSO assignments, destroying infrastructure, moving account to suspended OU.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Active — main view
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <SectionHeader icon={CheckCircle} title="Sandbox Environment" />
        <span style={activeBadgeStyle}>Active</span>
      </div>

      {/* Environment details */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <InfoCard label="Provider" value={env.provider.toUpperCase()} />
        <InfoCard label="Account ID" value={env.accountId || '—'} />
        <InfoCard label="Region" value={env.region || 'eu-west-1'} />
        <InfoCard label="Monthly Budget" value={env.budgetLimit ? `$${env.budgetLimit}` : '—'} />
        <InfoCard label="Provisioned" value={env.provisionedAt ? new Date(env.provisionedAt).toLocaleDateString() : '—'} />
        <InfoCard label="SSO Configured" value={env.ssoConfigured ? 'Yes' : 'No'} />
      </div>

      {/* Deployed App */}
      {env.deployUrl && (
        <div style={{ ...bannerStyle, background: '#f0fdf4', borderColor: '#bbf7d0', marginBottom: 24 }}>
          <Globe size={16} style={{ color: '#16a34a', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <strong style={{ color: '#166534' }}>Deployed App</strong>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <a href={env.deployUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: 14, textDecoration: 'underline' }}>
                {env.deployUrl}
              </a>
              <ExternalLink size={12} style={{ color: '#2563eb' }} />
            </div>
            {env.lastDeployedAt && (
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>
                Last deployed: {new Date(env.lastDeployedAt).toLocaleString()}
              </p>
            )}
          </div>
          {env.deployStatus === 'deploying' && (
            <Loader2 size={16} style={{ color: '#2563eb', animation: 'spin 1s linear infinite' }} />
          )}
        </div>
      )}

      {env.deployStatus === 'deploy_failed' && (
        <div style={{ ...bannerStyle, background: '#fef2f2', borderColor: '#fecaca', marginBottom: 24 }}>
          <AlertTriangle size={16} style={{ color: '#dc2626', flexShrink: 0 }} />
          <span style={{ color: '#991b1b', fontSize: 14 }}>Last deployment failed</span>
        </div>
      )}

      {/* Deploy history */}
      {env.deployHistory && env.deployHistory.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ fontSize: 14, fontWeight: 600, color: '#334155', marginBottom: 8 }}>Deploy History</h4>
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
            {env.deployHistory.slice().reverse().map((entry, i) => (
              <div key={entry.deployedAt} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: i < env.deployHistory!.length - 1 ? '1px solid #f1f5f9' : 'none', fontSize: 13 }}>
                {entry.status === 'deployed' && <CheckCircle size={14} style={{ color: '#16a34a' }} />}
                {entry.status === 'superseded' && <Clock size={14} style={{ color: '#94a3b8' }} />}
                {entry.status === 'failed' && <XCircle size={14} style={{ color: '#ef4444' }} />}
                <span style={{ color: '#334155' }}>{new Date(entry.deployedAt).toLocaleString()}</span>
                {entry.deployUrl && <a href={entry.deployUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', marginLeft: 'auto' }}>View</a>}
                <span style={{ color: '#94a3b8', fontSize: 11 }}>{entry.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SSO Profile */}
      {data?.ssoProfile && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: '#334155' }}>AWS CLI SSO Profile</h4>
            <button onClick={() => copyToClipboard(data.ssoProfile!)} style={linkBtnStyle}>
              <Copy size={14} /> {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <pre style={{ background: '#1e293b', color: '#e2e8f0', padding: 16, borderRadius: 8, fontSize: 12, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
            {data.ssoProfile}
          </pre>
        </div>
      )}

      {/* Console link */}
      {data?.consoleUrl && (
        <div style={{ marginBottom: 24 }}>
          <a href={data.consoleUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#2563eb', fontSize: 14, fontWeight: 500 }}>
            <Server size={14} /> Open AWS Console <ExternalLink size={12} />
          </a>
        </div>
      )}

      {/* Decommission */}
      {isAdmin && (
        <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 24, marginTop: 24 }}>
          <h4 style={{ fontSize: 14, fontWeight: 600, color: '#991b1b', marginBottom: 8 }}>Danger Zone</h4>
          <button
            onClick={handleDecommission}
            disabled={decommissionMutation.isPending}
            style={{ ...dangerBtnStyle }}
          >
            {decommissionMutation.isPending ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Decommissioning...</> : <><Trash2 size={16} /> Decommission Sandbox</>}
          </button>
          {decommissionMutation.isError && (
            <p style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{decommissionMutation.error.message}</p>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Shared components
// ============================================================================

const SectionHeader: React.FC<{ icon: React.ElementType; title: string; spinning?: boolean }> = ({ icon: Icon, title, spinning }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
    <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg, #00c7c4 0%, #0891b2 100%)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon size={18} color="white" style={spinning ? { animation: 'spin 1s linear infinite' } : undefined} />
    </div>
    <h3 style={{ fontSize: 18, fontWeight: 600, color: '#052831', margin: 0 }}>{title}</h3>
  </div>
);

const ProviderCard: React.FC<{ name: string; selected: boolean; disabled: boolean }> = ({ name, selected, disabled }) => (
  <div style={{
    padding: '12px 20px',
    border: `2px solid ${selected ? '#00c7c4' : '#e2e8f0'}`,
    borderRadius: 8,
    background: selected ? '#f0fdfa' : disabled ? '#f8fafc' : 'white',
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 14,
    fontWeight: 500,
    color: selected ? '#0f766e' : '#64748b',
    textAlign: 'center',
    flex: 1,
  }}>
    {name}
    {disabled && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Coming soon</div>}
  </div>
);

const InfoCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px' }}>
    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 500, color: '#1e293b' }}>{value}</div>
  </div>
);

// ============================================================================
// Styles
// ============================================================================

const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13, fontWeight: 500, color: '#334155', marginBottom: 6 };
const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' };
const bannerStyle: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', border: '1px solid', borderRadius: 8 };
const primaryBtnStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#00c7c4', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' };
const dangerBtnStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const linkBtnStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#2563eb', fontSize: 13, cursor: 'pointer', padding: 0 };
const activeBadgeStyle: React.CSSProperties = { background: '#dcfce7', color: '#16a34a', padding: '4px 12px', borderRadius: 999, fontSize: 13, fontWeight: 500 };
